import { ref, computed, watch } from 'vue';
import type { Locale } from 'vue-i18n';
import type { DropdownOption } from '@cvo/ui/components/molecules/Dropdown/Dropdown.model';
import type { FieldOption } from '@cvo/ui/types/fields';

export function useLangCurrency() {
  const { locale, locales, setLocale } = useI18n();
  const userStore = useUserStore();
  const { currencies, isLoading: isCurrenciesLoading } = useCurrencies();
  const route = useRoute();

  const selectedLanguage = ref<Locale | undefined>(locale.value);
  const selectedCurrency = ref<string | undefined>(userStore.currency);
  const isLoading = ref(false);

  // Language options mapped from available locales
  const languageOptions = computed<Array<DropdownOption<Locale>>>(() => {
    return locales.value.map(loc => ({
      label: loc.name ?? loc.code,
      value: loc.code,
    }));
  });

  const isCurrencyDisabled = computed<boolean>(() => {
    return !route.meta?.allowCurrencyChange || isCurrenciesLoading.value;
  });

  const isCurrencyVisible = computed<boolean>(() => {
    return !userStore.isActiveUser;
  });

  // Label of the language triggers, e.g. "EN | € EUR"; the currency only shows when it can be changed
  const currentLanguageLabel = computed<string>(() => {
    const langCode = locale.value?.split('-')?.[0]?.toUpperCase() || 'EN';
    if (!isCurrencyVisible.value) return langCode;

    const currentCurrency = currencyOptions.value.find((option) => option.value === userStore.currency);
    return `${langCode}\u00A0| ${currentCurrency?.label ?? '?'}`;
  });

  function getIntCurrencySymbol(currency: string): string {
    return new Intl.NumberFormat(selectedLanguage.value, {
      currency,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    }).formatToParts(1)
      .find((part) => part.type === 'currency')?.value ?? '';
  }

  const currencyOptions = computed<Array<FieldOption<string>>>(() => {
    return currencies.value.map((currency: string) => {
      const symbol = getIntCurrencySymbol(currency);
      return {
        // Intl falls back to the code when a currency has no symbol
        label: symbol === currency ? currency : `${symbol} ${currency}`,
        value: currency,
      };
    });
  });

  // Watch user store for locale changes
  watch(
    () => userStore.user?.locale,
    (newLocale, oldLocale) => {
      if (newLocale === oldLocale) return;

      if (newLocale && locales.value.some((loc) => loc.code === newLocale)) {
        selectedLanguage.value = newLocale as Locale;
        setLocale(newLocale as Locale);
      }
    },
  );

  // Watch user store for currency changes (including hydration from localStorage)
  watch(
    () => userStore.currency,
    (newCurrency) => {
      if (newCurrency && newCurrency !== selectedCurrency.value) {
        selectedCurrency.value = newCurrency;
      }
    },
    { immediate: true }, // Run immediately to catch hydrated values
  );

  function resetSelections() {
    selectedLanguage.value = locale.value;
    selectedCurrency.value = userStore.currency;
  }

  async function confirmChanges() {
    isLoading.value = true;
    try {
      if (selectedLanguage.value) {
        await setLocale(selectedLanguage.value);
      }

      if (selectedCurrency.value) {
        userStore.selectedCurrency = selectedCurrency.value;
      }
    } finally {
      isLoading.value = false;
    }
  }

  return {
    selectedLanguage,
    selectedCurrency,
    isLoading,
    languageOptions,
    currencyOptions,
    isCurrencyDisabled,
    isCurrencyVisible,
    currentLanguageLabel,
    resetSelections,
    confirmChanges,
  };
}
